import React, { useEffect, useState } from 'react';
import {
    Modal,
    Card,
    Switch,
    InputNumber,
    Button,
    Space,
    Typography,
    Tag,
    Row,
    Col,
    Divider,
    Select,
    Input,
    Alert,
    Tooltip,
    Table,
    Popconfirm,
    message
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    CheckCircleOutlined,
    StopOutlined,
    SortAscendingOutlined,
    TableOutlined,
    SettingOutlined,
    ThunderboltOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text, Paragraph } = Typography;

export interface SqlCheckItem {
    id: string;
    type: 'CONTAINS' | 'NOCONTAINS' | 'ORDEN' | 'DDL' | 'VISTA';
    value?: string; // Para CONTAINS y NOCONTAINS
    description: string;
    weight: number; // Peso orientativo en puntos si aplica
    active: boolean;
}

export interface SqlTestsConfig {
    ordenEstricte: boolean;
    validarRegistres: boolean;
    isDdl: boolean;
    isVista: boolean;
    penalitzacioPerIntent: number;
    items: SqlCheckItem[];
}

export const KEYWORDS_PRESETS = [
    { label: 'WHERE (Filtratge)', value: 'WHERE' },
    { label: 'JOIN / INNER JOIN (Enllaços de taules)', value: 'JOIN' },
    { label: 'LEFT JOIN (Enllaç extern esquerre)', value: 'LEFT JOIN' },
    { label: 'GROUP BY (Agrupació)', value: 'GROUP BY' },
    { label: 'HAVING (Condició sobre grups)', value: 'HAVING' },
    { label: 'ORDER BY (Ordenació explícita)', value: 'ORDER BY' },
    { label: 'DISTINCT (Eliminació de duplicats)', value: 'DISTINCT' },
    { label: 'LIKE (Coincidència de patrons)', value: 'LIKE' },
    { label: 'IN (Llista o subconsulta)', value: 'IN' },
    { label: 'NOT IN (Negació de pertinença)', value: 'NOT IN' },
    { label: 'EXISTS (Subconsulta correlacionada)', value: 'EXISTS' },
    { label: 'NOT EXISTS', value: 'NOT EXISTS' },
    { label: 'COUNT (Funció agregada de recompte)', value: 'COUNT' },
    { label: 'SUM (Suma de valors)', value: 'SUM' },
    { label: 'AVG (Mitjana)', value: 'AVG' },
    { label: 'MAX / MIN (Extrems)', value: 'MAX' },
    { label: 'LIMIT (Límit de resultats)', value: 'LIMIT' },
    { label: 'UNION / UNION ALL', value: 'UNION' },
    { label: 'CASE WHEN (Condicionals)', value: 'CASE' },
    { label: 'BETWEEN (Rang de valors)', value: 'BETWEEN' },
    { label: 'IS NULL / IS NOT NULL', value: 'IS NULL' }
];

export function parseSqlChecksString(rawChecks?: string): SqlTestsConfig {
    const config: SqlTestsConfig = {
        ordenEstricte: false,
        validarRegistres: true,
        isDdl: false,
        isVista: false,
        penalitzacioPerIntent: 0,
        items: []
    };

    if (!rawChecks || !rawChecks.trim()) return config;

    // 1. Mirar si conté bloc JSON estructurat (#SQL-TESTS#{...})
    const jsonMarker = '#SQL-TESTS#';
    const jsonIdx = rawChecks.indexOf(jsonMarker);
    if (jsonIdx !== -1) {
        try {
            const jsonPart = rawChecks.slice(jsonIdx + jsonMarker.length).trim();
            const parsed = JSON.parse(jsonPart);
            if (parsed && Array.isArray(parsed.items)) {
                return {
                    ordenEstricte: Boolean(parsed.ordenEstricte),
                    validarRegistres: parsed.validarRegistres !== undefined ? Boolean(parsed.validarRegistres) : true,
                    isDdl: Boolean(parsed.isDdl),
                    isVista: Boolean(parsed.isVista),
                    penalitzacioPerIntent: Number(parsed.penalitzacioPerIntent) || 0,
                    items: parsed.items
                };
            }
        } catch (e) {
            console.error("Error parsejant JSON de SQL-TESTS:", e);
        }
    }

    // 2. Parseig tradicional dels tokens separats per # (CONTAINS-xxx, NOCONTAINS-xxx, ORDEN, DDL-, VISTA-, PENALTY:x)
    const tokens = rawChecks.split('#').map(t => t.trim()).filter(Boolean);
    let idCounter = 1;

    tokens.forEach(tok => {
        if (tok.startsWith('PENALTY:')) {
            config.penalitzacioPerIntent = Number(tok.replace('PENALTY:', '')) || 0;
        } else if (tok === 'ORDEN') {
            config.ordenEstricte = true;
        } else if (tok.startsWith('DDL-')) {
            config.isDdl = true;
        } else if (tok.startsWith('VISTA-')) {
            config.isVista = true;
        } else if (tok.startsWith('CONTAINS-')) {
            const val = tok.replace('CONTAINS-', '').trim();
            if (val) {
                config.items.push({
                    id: `c_${idCounter++}`,
                    type: 'CONTAINS',
                    value: val,
                    description: `La query ha d'utilitzar el comandament '${val}'`,
                    weight: 10,
                    active: true
                });
            }
        } else if (tok.startsWith('NOCONTAINS-')) {
            const val = tok.replace('NOCONTAINS-', '').trim();
            if (val) {
                config.items.push({
                    id: `c_${idCounter++}`,
                    type: 'NOCONTAINS',
                    value: val,
                    description: `Prohibir l'ús de '${val}' a la consulta de l'alumne`,
                    weight: 10,
                    active: true
                });
            }
        }
    });

    return config;
}

export function serializeSqlChecksConfig(config: SqlTestsConfig): string {
    const legacyTokens: string[] = [];

    if (config.ordenEstricte) {
        legacyTokens.push('ORDEN');
    }
    if (config.isDdl) {
        legacyTokens.push('DDL-');
    }
    if (config.isVista) {
        legacyTokens.push('VISTA-');
    }

    config.items.forEach(item => {
        if (!item.active) return;
        if (item.type === 'CONTAINS' && item.value) {
            legacyTokens.push(`CONTAINS-${item.value.trim()}`);
        } else if (item.type === 'NOCONTAINS' && item.value) {
            legacyTokens.push(`NOCONTAINS-${item.value.trim()}`);
        }
    });

    if (config.penalitzacioPerIntent > 0) {
        legacyTokens.push(`PENALTY:${config.penalitzacioPerIntent}`);
    }

    // Acompanyar-ho de la representació JSON per permetre recarregar-lo visualment de forma 100% fidel
    const jsonStr = JSON.stringify(config);
    const legacyPart = legacyTokens.join('#');

    return legacyPart ? `${legacyPart}#SQL-TESTS#${jsonStr}` : `#SQL-TESTS#${jsonStr}`;
}

interface Props {
    visible: boolean;
    tipus?: string;
    exerciseName?: string;
    solutionRaw?: string;
    initialChecksStr?: string;
    onClose: (savedChecksStr?: string, savedConfig?: SqlTestsConfig) => void;
}

export const ConfiguracioTestsSqlModal: React.FC<Props> = ({
    visible,
    tipus,
    exerciseName = 'Exercici SQL',
    solutionRaw,
    initialChecksStr,
    onClose
}) => {
    const [config, setConfig] = useState<SqlTestsConfig>(() => parseSqlChecksString(initialChecksStr));
    const [newType, setNewType] = useState<'CONTAINS' | 'NOCONTAINS'>('CONTAINS');
    const [newKeyword, setNewKeyword] = useState<string>('');
    const [newCustomVal, setNewCustomVal] = useState<string>('');

    useEffect(() => {
        if (visible) {
            setConfig(parseSqlChecksString(initialChecksStr));
        }
    }, [visible, initialChecksStr]);

    const handleAddItem = () => {
        const valueToAdd = (newCustomVal || newKeyword || '').trim().toUpperCase();
        if (!valueToAdd) {
            message.warning("Indica o selecciona una paraula clau.");
            return;
        }

        // Comprovar si ja existeix
        const exists = config.items.some(it => it.type === newType && it.value?.toUpperCase() === valueToAdd);
        if (exists) {
            message.info("Aquesta comprovació ja està afegida a la llista.");
            return;
        }

        const desc = newType === 'CONTAINS'
            ? `Obligatori incloure '${valueToAdd}'`
            : `Prohibir l'ús de '${valueToAdd}'`;

        const newItem: SqlCheckItem = {
            id: `chk_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
            type: newType,
            value: valueToAdd,
            description: desc,
            weight: 10,
            active: true
        };

        setConfig(prev => ({
            ...prev,
            items: [...prev.items, newItem]
        }));

        setNewCustomVal('');
        setNewKeyword('');
    };

    const handleDeleteItem = (id: string) => {
        setConfig(prev => ({
            ...prev,
            items: prev.items.filter(it => it.id !== id)
        }));
    };

    const handleToggleItem = (id: string, active: boolean) => {
        setConfig(prev => ({
            ...prev,
            items: prev.items.map(it => it.id === id ? { ...it, active } : it)
        }));
    };

    const handleSave = () => {
        const finalChecksStr = serializeSqlChecksConfig(config);
        onClose(finalChecksStr, config);
    };

    const columns: ColumnsType<SqlCheckItem> = [
        {
            title: 'Estat',
            dataIndex: 'active',
            key: 'active',
            width: 70,
            align: 'center',
            render: (active, record) => (
                <Switch
                    size="small"
                    checked={active}
                    onChange={(checked) => handleToggleItem(record.id, checked)}
                />
            )
        },
        {
            title: 'Tipus de Comprovació',
            dataIndex: 'type',
            key: 'type',
            width: 140,
            render: (type) => (
                type === 'CONTAINS' ? (
                    <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontWeight: 600 }}>
                        REQUERIT (CONTAINS)
                    </Tag>
                ) : (
                    <Tag icon={<StopOutlined />} color="error" style={{ fontWeight: 600 }}>
                        PROHIBIT (NO CONTAINS)
                    </Tag>
                )
            )
        },
        {
            title: 'Element / Paraula Clau',
            dataIndex: 'value',
            key: 'value',
            width: 150,
            render: (val) => (
                <Tag color="geekblue" style={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: 13 }}>
                    {val}
                </Tag>
            )
        },
        {
            title: 'Descripció / Objectiu Pedagògic',
            dataIndex: 'description',
            key: 'description',
            render: (desc, record) => (
                <Input
                    size="small"
                    value={desc}
                    onChange={(e) => {
                        const newDesc = e.target.value;
                        setConfig(prev => ({
                            ...prev,
                            items: prev.items.map(it => it.id === record.id ? { ...it, description: newDesc } : it)
                        }));
                    }}
                />
            )
        },
        {
            title: 'Accions',
            key: 'accions',
            width: 80,
            align: 'center',
            render: (_, record) => (
                <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteItem(record.id)}
                />
            )
        }
    ];

    return (
        <Modal
            title={
                <Space>
                    <SettingOutlined style={{ color: '#722ed1', fontSize: 20 }} />
                    <span style={{ fontSize: 16, fontWeight: 700 }}>
                        Configuració de Tests i Regles SQL · {exerciseName}
                    </span>
                </Space>
            }
            open={visible}
            onCancel={() => onClose()}
            width={850}
            footer={[
                <Button key="cancel" onClick={() => onClose()}>
                    Cancel·lar
                </Button>,
                <Button key="save" type="primary" style={{ backgroundColor: '#722ed1' }} onClick={handleSave}>
                    Guardar Configuració de Tests
                </Button>
            ]}
        >
            <Alert
                message="Configuració visual de l'avaluació SQL"
                description="Defineix les regles que el jutge de base de dades validarà automàticament sobre la consulta de l'alumne (ordenació estricta, clàusules SQL obligatòries o prohibides)."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            <Row gutter={[16, 16]}>
                {/* 1. Comprovacions Globals */}
                <Col span={24}>
                    <Card size="small" title={<Space><ThunderboltOutlined style={{ color: '#faad14' }} /><Text strong>Comprovacions Generals de Resultats</Text></Space>}>
                        <Row gutter={[16, 12]}>
                            <Col span={12}>
                                <Space direction="vertical" size={2}>
                                    <Space>
                                        <Switch
                                            checked={config.ordenEstricte}
                                            onChange={(val) => setConfig(prev => ({ ...prev, ordenEstricte: val }))}
                                        />
                                        <Text strong>Exigir Ordre Exacte dels Registres (ORDER BY)</Text>
                                    </Space>
                                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 44 }}>
                                        Si s'activa, les files han de coincidir exactament en la mateixa posició que la solució oficial (etiqueta <code>ORDEN</code>).
                                    </Text>
                                </Space>
                            </Col>

                            <Col span={12}>
                                <Space direction="vertical" size={2}>
                                    <Space>
                                        <Text strong>Penalització per intent fallit:</Text>
                                        <InputNumber
                                            min={0}
                                            max={50}
                                            value={config.penalitzacioPerIntent}
                                            onChange={(val) => setConfig(prev => ({ ...prev, penalitzacioPerIntent: Number(val) || 0 }))}
                                            formatter={v => `${v}%`}
                                            parser={(v: any) => v.replace('%', '')}
                                            style={{ width: 90 }}
                                        />
                                    </Space>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Percentatge restat de la nota per cada intent incorrecte previ.
                                    </Text>
                                </Space>
                            </Col>
                        </Row>
                    </Card>
                </Col>

                {/* 2. Afegir Nova Regla (CONTAINS / NOCONTAINS) */}
                <Col span={24}>
                    <Card
                        size="small"
                        title={<Space><PlusOutlined style={{ color: '#52c41a' }} /><Text strong>Afegir Regla sobre el Codi SQL de l'Alumne</Text></Space>}
                    >
                        <Row gutter={[12, 12]} align="middle">
                            <Col span={6}>
                                <Select
                                    style={{ width: '100%' }}
                                    value={newType}
                                    onChange={(v) => setNewType(v)}
                                    options={[
                                        { label: 'Obligatori (CONTAINS)', value: 'CONTAINS' },
                                        { label: 'Prohibit (NOCONTAINS)', value: 'NOCONTAINS' }
                                    ]}
                                />
                            </Col>

                            <Col span={8}>
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Tria una clàusula habitual..."
                                    style={{ width: '100%' }}
                                    value={newKeyword || undefined}
                                    onChange={(v) => {
                                        setNewKeyword(v || '');
                                        if (v) setNewCustomVal('');
                                    }}
                                    options={KEYWORDS_PRESETS}
                                />
                            </Col>

                            <Col span={6}>
                                <Input
                                    placeholder="O escriu text personalitzat..."
                                    value={newCustomVal}
                                    onChange={(e) => {
                                        setNewCustomVal(e.target.value);
                                        if (e.target.value) setNewKeyword('');
                                    }}
                                    onPressEnter={handleAddItem}
                                />
                            </Col>

                            <Col span={4}>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleAddItem}
                                    block
                                >
                                    Afegir
                                </Button>
                            </Col>
                        </Row>
                    </Card>
                </Col>

                {/* 3. Llista de Regles ConfigTrades */}
                <Col span={24}>
                    <Card
                        size="small"
                        title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text strong>Regles i Comprovacions Actives ({config.items.length})</Text>
                                {config.items.length > 0 && (
                                    <Popconfirm
                                        title="Eliminar totes les regles?"
                                        onConfirm={() => setConfig(prev => ({ ...prev, items: [] }))}
                                        okText="Sí"
                                        cancelText="No"
                                    >
                                        <Button size="small" type="link" danger>Netejar tot</Button>
                                    </Popconfirm>
                                )}
                            </div>
                        }
                    >
                        {config.items.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '24px 0', color: '#999' }}>
                                <InfoCircleOutlined style={{ fontSize: 24, marginBottom: 8, color: '#d9d9d9' }} />
                                <div>No hi ha regles sintàctiques adicionals. L'exercici validarà que el resultat de la consulta coincideixi amb el de la solució.</div>
                            </div>
                        ) : (
                            <Table
                                size="small"
                                rowKey="id"
                                pagination={false}
                                columns={columns}
                                dataSource={config.items}
                            />
                        )}
                    </Card>
                </Col>
            </Row>
        </Modal>
    );
};

export default ConfiguracioTestsSqlModal;

